import { TenantBreakdownMonetaryValue, TenantBreakdownVacancy } from './TenantSummaryTables/types'
import { Box, Stack, useTheme } from '@mui/material'
import {
    AreaCell,
    AreaValue,
    AreaValueDataObject,
    EmptyCell,
    MonetaryValue,
    MonetaryValueDataObjectType,
    Percentage,
    decimalToLocalizedString,
    monetaryToLocalizedString,
    tokens,
} from 'ComponentLibrary'
import { TENANT_USAGE_TYPE_LABELS, TenantUsageTypeLabelKeys } from 'ComponentLibrary/src/common/tenant'
import { BaseValueCell } from 'ComponentLibrary/src/molecules/Table/Cells/BaseCells/DisplayCells'
import { Typography } from 'ComponentLibrary/src/piazza'
import { ReactNode } from 'react'
import { TenantDiscrepancyTooltip, TenantDiscrepancyTooltipCopy, TenantDiscrepancyTooltipInfo } from './TenantDiscrepancyTooltip'
import { WarningIcon } from '@phosphor-icons/react'

export const SummaryTenantTypeCell = ({
    type,
    bold,
    typeLabels,
}: {
    type?: TenantUsageTypeLabelKeys
    bold?: boolean
    typeLabels?: Partial<Record<TenantUsageTypeLabelKeys, string>>
}) => {
    if (!type) {
        return <EmptyCell />
    }

    return (
        <Typography variant="textS" color={tokens.ColorTextBold} bold={bold}>
            {typeLabels?.[type] ?? TENANT_USAGE_TYPE_LABELS[type]}
        </Typography>
    )
}

export const PercentageCell = ({
    percentage,
    additionalUnit,
    locale = 'de-DE',
}: {
    percentage?: number
    additionalUnit?: string
    locale?: string
}) => {
    const theme = useTheme()
    if (percentage === undefined) {
        return <EmptyCell />
    }

    return (
        <Stack justifyContent="flex-end" direction="row" gap={theme.spacing(1)}>
            <Stack>{decimalToLocalizedString(percentage * 100, false, 1, undefined, locale)}</Stack>
            <Stack color={theme.palette.customtext.black54}>%{additionalUnit && ` ${additionalUnit}`}</Stack>
        </Stack>
    )
}

export const UnitsCell = ({ units, unitLabel, bold }: { units?: number; unitLabel?: string; bold?: boolean }) => {
    return (
        <BaseValueCell
            value={units !== undefined ? units.toString() : undefined}
            unit={unitLabel || (units === 1 ? 'Einheit' : 'Einheiten')}
            bold={bold}
        />
    )
}

export const SummaryCellContainer = ({
    discrepancy,
    children,
    variant = 'cell',
    showBottomBorder = true,
    showLeftBorder = false,
    state,
    discrepancyTooltipCopy,
}: {
    discrepancy?: TenantDiscrepancyTooltipInfo
    children: ReactNode
    variant?: 'footer' | 'header' | 'cell'
    showBottomBorder?: boolean
    showLeftBorder?: boolean
    state?: 'warning' | 'negative' | 'positive'
    discrepancyTooltipCopy?: TenantDiscrepancyTooltipCopy
}) => {
    const highlightColors =
        state === 'warning' || discrepancy
            ? {
                  surface: tokens.ColorSurfaceWarningDefault,
                  border: tokens.ColorBorderWarning,
                  text: tokens.ColorTextWarningSubtle,
              }
            : state === 'positive'
              ? {
                    surface: tokens.ColorSurfacePositiveDefault,
                    border: tokens.ColorBorderPositive,
                    text: tokens.ColorTextPositiveSubtle,
                }
              : state === 'negative'
                ? {
                      surface: tokens.ColorSurfaceDangerDefault,
                      border: tokens.ColorBorderDanger,
                      text: tokens.ColorTextDangerSubtle,
                  }
                : undefined
    const shouldShowBorder = !!highlightColors && showBottomBorder

    return (
        <TenantDiscrepancyTooltip discrepancy={discrepancy} copy={discrepancyTooltipCopy}>
            <Stack
                direction="row"
                position="relative"
                padding={tokens.SizingSpacing100}
                bgcolor={highlightColors?.surface}
                height="70px"
                boxSizing="border-box"
                alignItems={variant === 'footer' ? 'flex-start' : variant === 'header' ? 'flex-end' : 'center'}
                borderBottom={shouldShowBorder ? `${tokens.SizingBorderS} solid ${highlightColors?.border}` : undefined}
                borderLeft={showLeftBorder ? `${tokens.SizingBorderM} solid ${tokens.ColorBorderDefault}` : undefined}
                sx={
                    highlightColors
                        ? {
                              '& .MuiTypography-root': {
                                  color: highlightColors?.text,
                              },
                          }
                        : undefined
                }
            >
                {discrepancy && (
                    <Box
                        sx={{
                            position: 'absolute',
                            right: tokens.SizingSpacing100,
                            top: tokens.SizingSpacing100,
                        }}
                    >
                        <WarningIcon size={tokens.SizingIconS} color={tokens.ColorIconWarning} weight="fill" />
                    </Box>
                )}
                {children}
            </Stack>
        </TenantDiscrepancyTooltip>
    )
}

export const UnitsSummaryCell = ({
    numberOfUnits,
    isParking,
    bold,
    grey,
    getUnitLabel,
    locale = 'de-DE',
}: {
    numberOfUnits: number
    isParking?: boolean
    bold?: boolean
    grey?: boolean
    getUnitLabel?: (count: number, isParking: boolean) => string
    locale?: string
}) => {
    return (
        <Stack direction="row" gap={tokens.SizingSpacing075}>
            <Typography color={grey ? tokens.ColorTextSubtle : tokens.ColorTextBold} variant="textS" bold={bold}>
                {decimalToLocalizedString(numberOfUnits, true, undefined, undefined, locale)}
            </Typography>
            <Typography color={tokens.ColorTextSubtle} variant="textS" bold={bold}>
                {getUnitLabel ? getUnitLabel(numberOfUnits, !!isParking) : isParking ? 'Stpl.' : 'EH'}
            </Typography>
        </Stack>
    )
}

export const TotalUnitsSummaryCell = ({
    numberOfUnits,
    numberOfParkingUnits,
    getUnitLabel,
    locale,
}: {
    numberOfUnits: number
    numberOfParkingUnits: number
    getUnitLabel?: (count: number, isParking: boolean) => string
    locale?: string
}) => {
    return (
        <Stack direction="column">
            <UnitsSummaryCell numberOfUnits={numberOfUnits} bold getUnitLabel={getUnitLabel} locale={locale} />
            <UnitsSummaryCell numberOfUnits={numberOfParkingUnits} isParking bold getUnitLabel={getUnitLabel} locale={locale} />
        </Stack>
    )
}

type AreaAndNumberOfUnitsBreakdownCellProps = {
    numberOfUnits?: number
    numberOfParkingUnits?: number
    area?: AreaValueDataObject
    bold?: boolean
    getUnitLabel?: (count: number, isParking: boolean) => string
    locale?: string
}

export const AreaAndNumberOfUnitsBreakdownCell = ({
    numberOfUnits,
    numberOfParkingUnits,
    area,
    bold,
    getUnitLabel,
    locale,
}: AreaAndNumberOfUnitsBreakdownCellProps) => {
    return (
        <Stack direction="column">
            {area && <AreaSummaryCell align="start" area={area} bold={bold} locale={locale} />}
            {numberOfUnits !== undefined && (
                <UnitsSummaryCell
                    numberOfUnits={numberOfUnits}
                    bold={bold}
                    grey={!bold && !!area}
                    getUnitLabel={getUnitLabel}
                    locale={locale}
                />
            )}
            {numberOfParkingUnits !== undefined && (
                <UnitsSummaryCell
                    numberOfUnits={numberOfParkingUnits}
                    isParking
                    bold={bold}
                    grey={!bold && !!area}
                    getUnitLabel={getUnitLabel}
                    locale={locale}
                />
            )}
        </Stack>
    )
}

type VacancyBreakdownCellProps = {
    numberOfUnits?: number
    numberOfParkingUnits?: number
    area?: AreaValueDataObject
    percentage?: number
    bold?: boolean
    getUnitLabel?: (count: number, isParking: boolean) => string
    locale?: string
}

export const VacancyBreakdownCell = ({
    numberOfUnits,
    numberOfParkingUnits,
    area,
    percentage,
    bold,
    getUnitLabel,
    locale,
}: VacancyBreakdownCellProps) => {
    return (
        <Stack direction="row" alignItems={bold ? 'flex-start' : 'center'} justifyContent="space-between" width="100%">
            <AreaAndNumberOfUnitsBreakdownCell
                numberOfUnits={numberOfUnits}
                numberOfParkingUnits={numberOfParkingUnits}
                area={area}
                bold={bold}
                getUnitLabel={getUnitLabel}
                locale={locale}
            />

            {percentage !== undefined && (
                <Typography color={tokens.ColorTextSubtle} variant="textS">
                    {new Percentage(percentage).format({ locale })}
                </Typography>
            )}
        </Stack>
    )
}

export const MonetaryCell = ({
    monetaryValue,
    additionalUnit = '',
    rounded,
    locale = 'de-DE',
}: {
    monetaryValue?: MonetaryValueDataObjectType
    additionalUnit?: string
    rounded?: boolean
    locale?: string
}) => {
    const theme = useTheme()
    if (!monetaryValue) {
        return <EmptyCell />
    }
    const monetaryValueString = monetaryToLocalizedString(monetaryValue, false, rounded, locale)
    return (
        <Stack justifyContent="flex-end" direction="row" gap={theme.spacing(1)}>
            <Stack>{monetaryValueString}</Stack>
            <Stack color={theme.palette.customtext.black54}>
                {monetaryValue.currency === 'EUR' ? '€' : monetaryValue.currency}
                {additionalUnit}
            </Stack>
        </Stack>
    )
}

export const VacancyCell = ({ vacancy, locale }: { vacancy?: TenantBreakdownVacancy; locale?: string }) => {
    if (!vacancy) {
        return <EmptyCell />
    }

    return (
        <Stack direction="column" width="100%">
            {vacancy.area && <AreaCell area={vacancy.area} />}
            {vacancy.percentage !== undefined && <PercentageCell percentage={vacancy.percentage} locale={locale} />}
            {vacancy.units !== undefined && <UnitsCell units={vacancy.units} />}
        </Stack>
    )
}

export const AreaSummaryCell = ({
    area,
    bold,
    align = 'end',
    locale,
}: {
    area?: AreaValueDataObject
    bold?: boolean
    align?: 'start' | 'end'
    locale?: string
}) => {
    if (!area) {
        return <EmptyCell textAlign={align === 'end' ? 'end' : 'start'} showDash />
    }

    const areaValue = AreaValue.fromDataObject(area)

    return (
        <Stack direction="row" gap={tokens.SizingSpacing075} justifyContent={align === 'end' ? 'flex-end' : 'flex-start'} width="100%">
            <Typography color={tokens.ColorTextBold} variant="textS" bold={bold}>
                {areaValue.format({ withUnit: false, minimumNumberOfDigits: 2, locale })}
            </Typography>
            <Typography color={tokens.ColorTextSubtle} variant="textS" bold={bold}>
                {areaValue.unitSymbol(locale)}
            </Typography>
        </Stack>
    )
}

export const RentSummaryCell = ({
    monetaryValue,
    bold,
    additionalUnit,
    grey,
    locale = 'de-DE',
}: {
    monetaryValue: MonetaryValueDataObjectType
    bold?: boolean
    additionalUnit: string
    grey?: boolean
    locale?: string
}) => {
    return (
        <Stack direction="row" gap={tokens.SizingSpacing075} justifyContent="flex-end" width="100%">
            <Typography color={grey ? tokens.ColorTextSubtle : tokens.ColorTextBold} variant="textS" bold={bold}>
                {MonetaryValue.fromMonetaryValueDataObject(monetaryValue).format({ withoutCurrency: true, locale })}
            </Typography>
            <Typography color={tokens.ColorTextSubtle} variant="textS" bold={bold}>
                €{additionalUnit}
            </Typography>
        </Stack>
    )
}

type TenantSummaryHeaderCellProps = {
    text?: string
    subheader?: string
    showLeftBorder?: boolean
}

export const TenantSummaryHeaderCell = ({ text, subheader, showLeftBorder }: TenantSummaryHeaderCellProps) => {
    return (
        <SummaryCellContainer variant="header" showLeftBorder={showLeftBorder}>
            <Stack direction="column" gap={tokens.SizingSpacing025} alignItems="flex-start">
                {text && (
                    <Typography variant="headline5" color={tokens.ColorTextBold}>
                        {text}
                    </Typography>
                )}
                {subheader && (
                    <Typography variant="headline5" color={tokens.ColorTextSubtle}>
                        {subheader}
                    </Typography>
                )}
            </Stack>
        </SummaryCellContainer>
    )
}

export const AreaVacancySummaryCell = ({
    vacancyPercentage,
    vacancyArea,
    bold,
    locale,
}: {
    vacancyPercentage: number
    vacancyArea?: AreaValueDataObject
    bold?: boolean
    locale?: string
}) => {
    if (!vacancyArea) {
        return <EmptyCell textAlign="end" showDash />
    }

    return (
        <Stack direction="row" alignItems={bold ? 'flex-start' : 'center'} justifyContent="space-between" width="100%">
            <Typography color={tokens.ColorTextSubtle} variant="textS">
                {new Percentage(vacancyPercentage).format({ locale })}
            </Typography>

            {vacancyArea && <AreaSummaryCell area={vacancyArea} bold={bold} locale={locale} />}
        </Stack>
    )
}

export const NumberOfUnitsVacancySummaryCell = ({
    vacancyUnits,
    vacancyParkingUnits,
    bold,
    getUnitLabel,
    locale,
}: {
    vacancyUnits?: number
    vacancyParkingUnits?: number
    bold?: boolean
    getUnitLabel?: (count: number, isParking: boolean) => string
    locale?: string
}) => {
    return (
        <Stack direction="column" alignItems="flex-end" justifyContent={bold ? 'flex-start' : 'center'} width="100%">
            {vacancyUnits !== undefined && (
                <UnitsSummaryCell numberOfUnits={vacancyUnits} bold={bold} getUnitLabel={getUnitLabel} locale={locale} />
            )}
            {vacancyParkingUnits !== undefined && (
                <UnitsSummaryCell numberOfUnits={vacancyParkingUnits} isParking bold={bold} getUnitLabel={getUnitLabel} locale={locale} />
            )}
        </Stack>
    )
}

export const MonetaryBreakdownCell = ({
    monetaryBreakdown,
    isParking,
    bold,
    copy,
    locale,
}: {
    monetaryBreakdown?: TenantBreakdownMonetaryValue
    isParking?: boolean
    bold?: boolean
    locale?: string
    copy?: {
        perYear: string
        perSquareMeterPerMonth: string
        perUnitPerMonth: string
        perParkingSpacePerMonth: string
    }
}) => {
    if (!monetaryBreakdown) {
        return <EmptyCell />
    }

    return (
        <Stack direction="column" width="100%">
            {monetaryBreakdown.total && (
                <RentSummaryCell
                    monetaryValue={monetaryBreakdown.total}
                    additionalUnit={copy?.perYear ?? '/Jahr'}
                    bold={bold}
                    locale={locale}
                />
            )}
            {monetaryBreakdown.per_sqm_monthly && (
                <RentSummaryCell
                    monetaryValue={monetaryBreakdown.per_sqm_monthly}
                    additionalUnit={copy?.perSquareMeterPerMonth ?? '/m² p.M.'}
                    bold={bold}
                    grey
                    locale={locale}
                />
            )}
            {monetaryBreakdown.per_unit_monthly && (
                <RentSummaryCell
                    monetaryValue={monetaryBreakdown.per_unit_monthly}
                    additionalUnit={isParking ? (copy?.perParkingSpacePerMonth ?? '/Stpl. p.M.') : (copy?.perUnitPerMonth ?? '/EH p.M.')}
                    bold={bold}
                    grey
                    locale={locale}
                />
            )}
        </Stack>
    )
}

type DeltaBreakdownCellProps = {
    delta?: MonetaryValueDataObjectType
    percentage?: number
    bold?: boolean
    perYearLabel?: string
    locale?: string
}

export const DeltaBreakdownCell = ({ delta, percentage, bold, perYearLabel, locale }: DeltaBreakdownCellProps) => {
    return (
        <Stack direction="row" width="100%" justifyContent="space-between">
            {percentage !== undefined ? (
                <Typography color={tokens.ColorTextSubtle} variant="textS">
                    {new Percentage(percentage).format({ locale })}
                </Typography>
            ) : (
                <div />
            )}
            {delta && <RentSummaryCell monetaryValue={delta} additionalUnit={perYearLabel ?? '/Jahr'} bold={bold} locale={locale} />}
        </Stack>
    )
}
